import LegalShell from '../../components/legal/LegalShell';
import { CONSENT_VERSIONS } from '../../auth/useConsent';

export default function Terms() {
  return (
    <LegalShell title="Terms of Service" version={CONSENT_VERSIONS.tos} effective="2026-08-06">
      <p>
        These Terms of Service ("Terms") govern your access to and use of the
        OculusMind.AI website and the OculusMind.AI AI-agent platform
        (collectively, the "Services"). The Services are operated by Andrew
        Paris LLC, d/b/a MediaShare-Link ("Company," "we," "us," "our"). By creating an account,
        clicking to accept these Terms,
        submitting a prompt or message to an agent as a guest, or otherwise
        accessing or using the Services, you ("you," "your," or "User") agree
        to be bound by these Terms. If you do not agree to these Terms, you may
        not access or use the Services.
      </p>
      <p>
        These Terms incorporate by reference our{' '}
        <a href="/legal/privacy">Privacy Policy</a> and our{' '}
        <a href="/legal/ai-consent">AI Consent Notice</a>, each of which describes how
        we collect, use, route, and protect your data. Where these Terms and
        those documents address the same subject, read them together; the
        broader restriction or disclosure controls.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        The Services are available only to individuals who are eighteen (18)
        years of age or older and who have the legal capacity to enter into a
        binding contract. By using the Services, you represent and warrant that
        you meet these requirements, that the registration information you
        provide is accurate and complete, and that your use of the Services does
        not violate any law or regulation applicable to you. If you are using
        the Services on behalf of an organization, you represent that you are
        authorized to bind that organization to these Terms, and "you" refers to
        both you and that organization.
      </p>

      <h2>2. Description of the Services</h2>
      <p>
        The Services provide access to generative-artificial-intelligence
        models and AI agents that hold conversations, follow instructions,
        process files you upload, retrieve information from external sources on
        your behalf, and generate text and other output. The Services are
        provided for your use subject to these Terms, the usage limits we set,
        and the data-handling practices described in our Privacy Policy and AI
        Consent Notice. Portions of the Services — including guest and
        free-tier access — are supported by advertising, and we may display
        advertisements (including interest-based advertisements served by
        third-party advertising providers) alongside the Services as described
        in our Privacy Policy. We may add, modify, suspend, or
        discontinue any feature of the Services, in whole or in part, at any
        time and without liability to you.
      </p>

      <h2>3. Accounts and Security</h2>
      <p>
        To use most features of the Services you must create an account. You are
        responsible for maintaining the confidentiality of your credentials and
        for all activity that occurs under your account, whether or not
        authorized by you. You agree to:
      </p>
      <ul>
        <li>Provide accurate, current, and complete registration information.</li>
        <li>Keep your password and any multi-factor authentication factors secure and confidential.</li>
        <li>Not share your account, sell access to it, or permit another person to use it.</li>
        <li>Notify us promptly at the contact address below of any unauthorized use of your account or any other breach of security.</li>
      </ul>
      <p>
        We are not liable for any loss or damage arising from your failure to
        safeguard your account or credentials.
      </p>

      <h2>4. Right to Refuse or Deny Service</h2>
      <p>
        We reserve the right to refuse, deny, suspend, restrict, or terminate
        access to the Services — to any person or entity, at any time, for any
        reason or for no reason, and with or without notice — in our sole and
        absolute discretion. This includes the right to decline to create an
        account, to reject any registration, to limit available features, and to
        discontinue serving any User. Our exercise of this right does not require
        cause and does not give rise to any liability to you or to any third
        party. Nothing in these Terms obligates us to provide the Services to any
        particular person.
      </p>

      <h2>5. Acceptable Use</h2>
      <p>
        You agree to use the Services only for lawful purposes and in compliance
        with these Terms and all applicable laws and regulations. Without
        limiting the generality of the foregoing, you agree that you will not,
        and will not attempt to, and will not permit or encourage others to:
      </p>
      <ul>
        <li>
          Use the Services for any unlawful, fraudulent, deceptive, or malicious
          purpose, or in violation of any applicable local, state, national, or
          international law or regulation.
        </li>
        <li>
          Create, upload, generate, solicit, store, or distribute content that
          is illegal, including child sexual abuse material, content that
          sexually exploits or endangers minors, content that facilitates human
          trafficking, or content that infringes another party's intellectual
          property or other rights.
        </li>
        <li>
          Use the Services to plan, promote, facilitate, or carry out violence,
          terrorism, self-harm, harassment, stalking, threats, or the
          exploitation, abuse, or endangerment of any person.
        </li>
        <li>
          Generate or disseminate malware, ransomware, or other malicious code;
          conduct phishing, credential theft, or social-engineering attacks; or
          attempt to gain unauthorized access to any system, account, network,
          or data.
        </li>
        <li>
          Use the Services to develop, design, or facilitate weapons, including
          chemical, biological, radiological, nuclear, or high-yield explosive
          weapons, or other activities that pose a significant risk of physical
          harm.
        </li>
        <li>
          Engage in fraud, market manipulation, money laundering, the sale of
          regulated or prohibited goods, or other illicit commercial activity.
        </li>
        <li>
          Generate content intended to deceive, including impersonation,
          disinformation campaigns, fabricated identities, or material designed
          to be passed off as authored by another person or entity without
          authorization.
        </li>
        <li>
          Infringe, misappropriate, or violate the intellectual-property,
          privacy, publicity, contractual, or other rights of any third party,
          or upload content you do not have the right to provide.
        </li>
        <li>
          Reverse engineer, decompile, disassemble, scrape, or otherwise attempt
          to discover the source code, models, or underlying components of the
          Services, except to the extent this restriction is prohibited by law.
        </li>
        <li>
          Circumvent, disable, or interfere with safety filters, rate limits,
          usage quotas, authentication, or other technical or security measures
          of the Services.
        </li>
        <li>
          Use automated means to access the Services in a manner that imposes an
          unreasonable or disproportionate load on our infrastructure, or that
          degrades the Services for other Users.
        </li>
        <li>
          Use the output of the Services to train, fine-tune, or develop a
          competing AI model or service, except as expressly permitted by us in
          writing.
        </li>
        <li>
          Use the Services in any other manner that we determine, in our sole
          discretion, to be inappropriate, abusive, harmful, or contrary to the
          purpose of the Services.
        </li>
      </ul>
      <p>
        We may, but are not obligated to, monitor, review, filter, or moderate
        use of the Services and the content submitted to or generated by them.
        Automated and human review are imperfect, and we do not guarantee that
        prohibited use will be detected or prevented.
      </p>

      <h2>6. Enforcement, Termination, and Reporting</h2>
      <p>
        If we determine, in our sole discretion, that you have engaged in any
        illegal or inappropriate use of the Services, or have otherwise violated
        these Terms, we may take any one or more of the following actions, with
        or without prior notice and without liability to you:
      </p>
      <ul>
        <li>
          <strong>Terminate or suspend your account</strong> and your access to
          the Services, in whole or in part, immediately and permanently.
        </li>
        <li>
          <strong>Delete all data associated with your account</strong> —
          including your conversation history, prompts, uploaded files, agent
          configuration, and other content — at our option and without any
          obligation to preserve, return, or provide a copy of that data to you,
          except where applicable law requires us to retain it.
        </li>
        <li>
          <strong>Preserve and disclose information to law-enforcement and
          governmental authorities.</strong> We may forward any and all
          information relating to you, your account, and your use of the
          Services — including your identity, registration details, technical
          identifiers, conversation history, prompts, uploaded files, agent
          activity, and usage and access logs — to law-enforcement officials,
          regulators, or other governmental authorities where we believe, in our
          sole discretion, that doing so is appropriate or necessary to
          investigate, prevent, or respond to suspected illegal activity, to
          protect the safety of any person, to protect our rights or property,
          or to comply with applicable law, legal process, or a governmental
          request.
        </li>
        <li>
          <strong>Pursue any other remedy</strong> available to us at law or in
          equity, including referral for civil or criminal prosecution.
        </li>
      </ul>
      <p>
        You consent to the actions described in this section as a condition of
        your use of the Services. Termination of your account does not relieve
        you of any obligations incurred prior to termination, and the provisions
        of these Terms that by their nature should survive termination — including
        Sections 7 through 14 — will survive.
      </p>

      <h2>7. Your Content and License</h2>
      <p>
        "User Content" means the prompts, instructions, messages, files, and
        other materials you submit to the Services, and "Output" means the
        content the Services generate in response. As between you and us, you
        retain whatever ownership rights you hold in your User Content. You grant
        us a worldwide, non-exclusive, royalty-free license to host, store,
        reproduce, process, transmit, modify, and create derivative works of your
        User Content and Output to the extent necessary to operate, provide,
        secure, and improve the Services, to enforce these Terms, and to comply
        with applicable law.
      </p>
      <p>
        You are solely responsible for your User Content and for ensuring that
        you have all rights necessary to submit it and to authorize the
        processing described above. You represent and warrant that your User
        Content does not violate these Terms, any law, or any third party's
        rights. Subject to your compliance with these Terms, and to the extent we
        hold any rights in the Output, we assign to you our rights in the Output
        generated for you; you acknowledge that Output may not be unique, that
        similar or identical Output may be generated for other Users, and that
        you may not hold any exclusive rights in Output.
      </p>

      <h2>8. AI Output; Entertainment Purposes Only; No Professional Advice</h2>
      <p>
        The Services rely on generative-AI models. AI-generated Output may be
        inaccurate, incomplete, out of date, biased, or entirely fabricated
        ("hallucinated"), even when it appears confident or authoritative. You
        are responsible for evaluating the accuracy, completeness, legality, and
        suitability of any Output before relying on or acting upon it.
      </p>
      <p>
        <strong>Entertainment purposes only.</strong> You understand and agree
        that the Services, and any Output produced by the Services or by any
        service made available on the site, are provided for general
        informational and entertainment purposes only. Output does not
        constitute, and must not be treated as, medical advice, financial,
        investment, or tax advice, legal advice, or professional advice of any
        kind, and no professional relationship of any kind is created by your use
        of the Services. The Services do not provide professional advice and are
        not a substitute for the judgment of a qualified professional. Your use
        of any Output is at your sole risk, and you will not rely on Output as a
        sole source of truth or factual information, or as a substitute for
        professional advice. Output must not be relied upon for medical, legal,
        financial, tax, mental-health, safety-critical, or other decisions
        requiring professional judgment. You assume all risk arising from your
        use of, or reliance on, any Output.
      </p>
      <p>
        <strong>You hold all responsibility.</strong> You agree that you will
        not take, or refrain from taking, any action — and will not make any
        decision — based on or in reliance upon any Output in any manner that
        creates or could create potential liability for the Company or the
        Services. You acknowledge and agree that you bear sole and complete
        responsibility for any action you take, or decline to take, in
        connection with the Services or any Output, and for all consequences of
        that action or inaction. To the fullest extent permitted by law, you
        release the Company and the Services from, and agree to hold all such
        responsibility yourself with respect to, any loss, damage, claim, or
        liability arising out of or relating to your use of, or reliance on, any
        Output. Accordingly, you are solely responsible for, and agree to bear,
        any losses, costs, or financial damages that you, the Company, the
        Services, or their owners incur arising out of or relating to your
        actions on the site or your use of or reliance on any Output, and you
        agree to reimburse and indemnify the Company for any such amounts it
        incurs, as further described in Section 13 (Indemnification). This
        Section is in addition to, and does not limit, the disclaimers,
        limitation of liability, and indemnification provisions in Sections 11
        through 13.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        The Services, including all software, models, interfaces, text,
        graphics, logos, and other materials we provide (excluding your User
        Content and the Output assigned to you), are owned by or licensed to the
        Company and are protected by intellectual-property and other laws. We
        grant you a limited, revocable, non-exclusive, non-transferable,
        non-sublicensable license to access and use the Services for their
        intended purpose, subject to these Terms. All rights not expressly
        granted are reserved. "OculusMind.AI," our logos, and our other marks are
        trademarks of the Company; you may not use them without our prior written
        permission.
      </p>

      <h2>10. Third-Party Services and External Content</h2>
      <p>
        The Services rely on third-party providers and, at your direction, may
        retrieve information from external websites, search engines, and
        third-party APIs. We do not control and are not responsible for the
        availability, accuracy, content, or data practices of third parties.
        Your use of third-party services may be governed by their own terms and
        policies, and your interactions with them are solely between you and the
        relevant third party. As described in our Privacy Policy and AI Consent
        Notice, portions of your inputs may be transmitted to external services
        as a side-effect of performing tasks you request. Our use of cookies and
        similar tracking technologies, and the choices available to you, are
        described separately in our{' '}
        <a href="/legal/cookie-policy">Cookie Policy</a>.
      </p>

      <h2>11. Disclaimer of Warranties</h2>
      <p>
        THE SERVICES, INCLUDING ALL OUTPUT, ARE PROVIDED "AS IS" AND "AS
        AVAILABLE," WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY KIND. TO THE
        FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, WHETHER
        EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING ANY IMPLIED
        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
        AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT OF COURSE OF DEALING
        OR USAGE OF TRADE. WE DO NOT WARRANT THAT THE SERVICES WILL BE
        UNINTERRUPTED, SECURE, ERROR-FREE, OR THAT ANY OUTPUT WILL BE ACCURATE OR
        RELIABLE. YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT WILL THE COMPANY OR
        ITS OWNERS, OFFICERS, EMPLOYEES, AGENTS, OR SUPPLIERS BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE
        DAMAGES, OR FOR ANY LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR OTHER
        INTANGIBLE LOSSES, ARISING OUT OF OR RELATING TO YOUR USE OF, OR
        INABILITY TO USE, THE SERVICES, WHETHER BASED ON WARRANTY, CONTRACT,
        TORT (INCLUDING NEGLIGENCE), STATUTE, OR ANY OTHER THEORY, EVEN IF WE
        HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL
        AGGREGATE LIABILITY ARISING OUT OF OR RELATING TO THESE TERMS OR THE
        SERVICES WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR
        THE SERVICES IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO
        THE LIABILITY, OR (B) ONE HUNDRED U.S. DOLLARS ($100). SOME JURISDICTIONS
        DO NOT ALLOW CERTAIN OF THESE LIMITATIONS, SO SOME OF THEM MAY NOT APPLY
        TO YOU.
      </p>

      <h2>13. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless the Company and its
        owners, officers, employees, and agents from and against any claims,
        liabilities, damages, losses, and expenses, including reasonable
        attorneys' fees, arising out of or in any way connected with: (a) your
        access to or use of the Services; (b) your User Content; (c) your
        violation of these Terms or of any law or third-party right; (d) your
        use of, reliance on, or misuse of any Output; or (e) any action you take,
        or decline to take, based on any Output. We reserve the right to assume
        the exclusive
        defense and control of any matter subject to indemnification by you, in
        which case you agree to cooperate with our defense of that matter.
      </p>

      <h2>14. Governing Law and Dispute Resolution</h2>
      <p>
        These Terms and any dispute arising out of or relating to them or the
        Services are governed by the laws of the State of Texas, without regard
        to its conflict-of-laws principles. Subject to any applicable arbitration
        or small-claims provisions, you and we agree that the exclusive venue for
        any dispute will be the state and federal courts located in Travis
        County, Texas, and you consent to the personal jurisdiction of those
        courts. To the extent permitted by law, any claim must be brought within
        one (1) year after it arises, or it is permanently barred. To the extent
        permitted by law, you and we waive any right to a jury trial and any
        right to participate in a class action.
      </p>

      <h2>15. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. When we do, we will revise
        the "Effective" date shown at the top of this document and, where the
        change is material, provide additional notice through the Services or by
        email. Your continued use of the Services after the effective date of an
        updated version constitutes your acceptance of the updated Terms. If you
        do not agree to the updated Terms, you must stop using the Services.
      </p>

      <h2>16. Subscriptions, Fees, and Billing</h2>
      <p>
        <strong>(a) Paid plans.</strong> Certain features of the Services require
        a paid subscription. The available plans, the features they include, their
        prices, and their billing periods (for example, monthly or annual) are
        described on our pricing page and are presented to you before you
        purchase. Unless stated otherwise, all fees are quoted and charged in U.S.
        dollars.
      </p>
      <p>
        <strong>(b) Authorization and automatic renewal.</strong> When you purchase
        a subscription, you authorize us and our payment processor to charge your
        payment method the applicable fee. <strong>Subscriptions renew
        automatically</strong> at the end of each billing period at the
        then-current price, and your payment method will be charged for each
        renewal, until you cancel. By subscribing, you consent to these recurring
        charges.
      </p>
      <p>
        <strong>(c) Cancellation.</strong> You may cancel a subscription at any
        time from the billing portal linked in your account settings. Cancellation
        takes effect at the end of your current paid billing period: you keep
        access to paid features until then, and the plan does not renew afterward.
        Except where required by law, we do not provide refunds or credits for
        partial billing periods or unused portions of a plan.
      </p>
      <p>
        <strong>(d) Free and guest access.</strong> We also offer free and guest
        access with lower usage allowances and a reduced feature set. Free and
        guest access is provided &ldquo;as is,&rdquo; is subject to usage limits,
        and may be changed, limited, or discontinued at any time.
      </p>
      <p>
        <strong>(e) Usage allowances and metering.</strong> Each plan &mdash;
        including free and guest access &mdash; includes a <strong>daily usage
        allowance</strong> measured in OculusMind tokens (&ldquo;OM&rdquo;). Your
        OM allowance is consumed by your use of the Services, including the
        messages and instructions you send, the agents&rsquo; responses, the tools
        and actions the agents perform on your behalf, and outbound messages such
        as email and SMS. When you reach your daily allowance, additional requests
        are paused until the allowance resets (generally at the start of the next
        day in your local time zone); we do not automatically charge you for use
        beyond your plan&rsquo;s allowance. The size of the allowances, the amount
        of OM attributed to particular actions, and the method by which OM is
        calculated are set by us and <strong>may be adjusted from time to
        time</strong>. Higher-tier plans provide larger daily allowances.
      </p>
      <p>
        <strong>(f) Price and plan changes.</strong> We may change our fees and the
        features included in a plan. For a recurring subscription, any change to
        your recurring fee takes effect on your next renewal after we provide
        notice (for example, through the Services or by email), and continuing your
        subscription after the change takes effect constitutes your acceptance of
        the new fee.
      </p>
      <p>
        <strong>(g) Failed payments.</strong> If a charge is declined or otherwise
        fails, we may retry the charge and may suspend, downgrade, or terminate
        your access to paid features until payment is successfully processed.
      </p>
      <p>
        <strong>(h) Taxes.</strong> Fees are exclusive of taxes. You are
        responsible for any sales, use, value-added, or similar taxes applicable to
        your subscription, other than taxes based on our net income.
      </p>
      <p>
        <strong>(i) Payment processor.</strong> Payments are processed by our
        third-party payment processor (currently Stripe), and that processing is
        subject to the processor&rsquo;s own terms and privacy policy. We do not
        store your full payment card number.
      </p>

      <h2>17. Termination by You</h2>
      <p>
        You may stop using the Services and delete your account at any time
        through the Settings within the Services. Account deletion is handled as
        described in our Privacy Policy. Termination does not entitle you to a
        refund of any amounts already paid, except as required by law.
      </p>

      <h2>18. Miscellaneous</h2>
      <p>
        These Terms, together with the Privacy Policy and AI Consent Notice,
        constitute the entire agreement between you and us regarding the Services
        and supersede any prior agreements. If any provision of these Terms is
        held to be unenforceable, that provision will be limited or severed to
        the minimum extent necessary, and the remaining provisions will remain in
        full force and effect. Our failure to enforce any provision is not a
        waiver of our right to do so later. You may not assign or transfer these
        Terms without our prior written consent; we may assign these Terms freely.
        We will not be liable for any delay or failure to perform resulting from
        causes beyond our reasonable control. Nothing in these Terms creates any
        partnership, joint venture, agency, or employment relationship between
        you and us.
      </p>

      <h2>Contact Information</h2>
      <address>
        Andrew Paris LLC, d/b/a MediaShare-Link<br />
        5900 Balcones Dr Suite 21007<br />
        Austin, TX 78731<br />
        United States
      </address>
      <p>
        Email: <a href="mailto:Help@OculusMind.AI">Help@OculusMind.AI</a>
      </p>
    </LegalShell>
  );
}
